import type { Tile } from './types';

/**
 * docs/79: a unit's body on the board. `tile` is its origin (top-left square) and it covers
 * `size × size` squares from there, the same as `fk_sim::board::Footprint`. Every client question
 * about where a unit is (what a square holds, where to draw it, where a walk passes) goes through
 * here so a huge foe is measured the same way everywhere.
 */
export interface Bodied { tile: Tile; size?: number | undefined }

/** The side of its square, never below 1. */
export const sizeOf = (u: { size?: number | undefined }): number => Math.max(1, Math.floor(u.size ?? 1));

/** Whether the body standing with its origin at `origin` covers `t`. */
export const coversAt = (origin: Tile, size: number, t: Tile): boolean =>
  t.x >= origin.x && t.x < origin.x + size && t.y >= origin.y && t.y < origin.y + size;

/** Whether the unit's body covers `t`. */
export const covers = (u: Bodied, t: Tile): boolean => coversAt(u.tile, sizeOf(u), t);

/** The covered squares from `origin`, `(y, x)` order. */
export function tilesAt(origin: Tile, size: number): Tile[] {
  const out: Tile[] = [];
  for (let y = origin.y; y < origin.y + size; y++) for (let x = origin.x; x < origin.x + size; x++) out.push({ x, y });
  return out;
}

/** The unit's covered squares, `(y, x)` order. */
export const bodyTiles = (u: Bodied): Tile[] => tilesAt(u.tile, sizeOf(u));

/** The board-pixel centre of a body standing at `origin` (for size 1, the square's centre). */
export const bodyCentre = (origin: Tile, size: number, cell: number): { x: number; y: number } => ({
  x: (origin.x + size / 2) * cell,
  y: (origin.y + size / 2) * cell,
});

/** The square-unit centre of a body at `origin` (fractional for even sizes). */
export const centreTile = (origin: Tile, size: number): { x: number; y: number } => ({
  x: origin.x + (size - 1) / 2,
  y: origin.y + (size - 1) / 2,
});

/** The first unit (in list order) whose body covers `t`. */
export function unitCovering<U extends Bodied>(units: readonly U[], t: Tile, keep: (u: U) => boolean = () => true): U | undefined {
  return units.find((u) => keep(u) && covers(u, t));
}

/** Manhattan gap between the nearest covered square and `t` (0 inside), as `Footprint::gap_to`. */
export function gapTo(origin: Tile, size: number, t: Tile): number {
  const dx = Math.max(origin.x - t.x, t.x - (origin.x + size - 1), 0);
  const dy = Math.max(origin.y - t.y, t.y - (origin.y + size - 1), 0);
  return dx + dy;
}

/**
 * Which reachable origin a pointer over `hover` means for a body of `size` (docs/79 HU5): of the
 * offered origins whose footprint covers the square, the one whose centre is nearest `pointer`
 * (square units, defaults to the hovered square's centre), then the cheapest walk (`offered`
 * gives an origin's cost, `undefined` where the body cannot stop), then `(y, x)` — so pointing
 * beside a huge hero steps it there rather than round a corner. For one square, the square
 * itself. `undefined` when no offered footprint covers it.
 */
export function originFor(hover: Tile, size: number, offered: (t: Tile) => number | undefined, pointer?: { x: number; y: number }): Tile | undefined {
  if (size <= 1) return offered(hover) !== undefined ? hover : undefined;
  const p = pointer ?? { x: hover.x, y: hover.y };
  let best: { t: Tile; d: number; cost: number } | undefined;
  for (let oy = hover.y - size + 1; oy <= hover.y; oy++) {
    for (let ox = hover.x - size + 1; ox <= hover.x; ox++) {
      const t = { x: ox, y: oy };
      const cost = offered(t);
      if (cost === undefined) continue;
      const c = centreTile(t, size);
      const d = (c.x - p.x) ** 2 + (c.y - p.y) ** 2;
      if (!best || d < best.d - 1e-9 || (Math.abs(d - best.d) <= 1e-9 && cost < best.cost)) best = { t, d, cost };
    }
  }
  return best?.t;
}

/**
 * docs/79: a huge body's Move reach as squares — every square some stopping origin's footprint
 * covers, at the cheapest such origin's cost (what the field washes; the click picks the origin).
 */
export function bodyStops<R extends { tile: Tile; cost: number }>(stops: readonly R[], n: number): Array<{ tile: Tile; cost: number }> {
  const best = new Map<string, { tile: Tile; cost: number }>();
  for (const r of stops) for (const t of tilesAt(r.tile, n)) {
    const k = `${t.x},${t.y}`, had = best.get(k);
    if (!had || r.cost < had.cost) best.set(k, { tile: t, cost: r.cost });
  }
  return [...best.values()].sort((a, b) => a.tile.y - b.tile.y || a.tile.x - b.tile.x);
}
